import { useState, useEffect } from "react";

import ErrorMessage from "../../../components/ErrorMessage/ErrorMessage";
import Pagination from "../../../components/Pagination/Pagination";
import {
  ReportActionButtons,
  SECTION_KEYS,
} from "../../screened-manuscripts/components/ReportActionButtons";
import {
  ApplyAction,
  ColorizeStatusText,
} from "../../../components/ApplyAction";
import {
  convertISOToReadableDate,
  getAmbientScreeningSubmissionById,
  showErrorToast,
} from "../../../helpers";
import { DataTable, useLoader } from "stm-frontend-library";
import { EmptyInbox, SignalReportModal } from "../../../components";
import { dropdownOptions } from "../dropdownOptions";
import { columns } from "../columns";
import useDuplicateSubmissions from "../hooks/useDuplicateSubmissions";
import useAmbientScreenedManuscripts from "../../screened-manuscripts/hooks/useAmbientScreenedManuscripts";
import { useAppContext } from "../../../components/AppContext";
import { ModalWrapper } from "../../../components/ModalWrapper/ModalWrapper";

const ArchivedManuscriptsTab = ({ type, isActiveTab }) => {
  const { searchInput } = useAppContext();

  const [selectedItem, setSelectedItem] = useState({
    index: null,
    visibleSection: SECTION_KEYS.ALL,
  });

  const { showLoader, hideLoader, isLoading } = useLoader();
  const [fetchedData, setFetchedData] = useState(null);

  const isInboxTabSelected = type === "wo_status";

  const {
    data,
    setData,
    fetchData,
    setTriggerOrMatchStatus,
    error,
    pageCount,
    currentPage,
    setCurrentPage,
    searchTerm,
    setSearchTerm,
  } = useDuplicateSubmissions({ type, isActiveTab });

  const { setSubmissionStatus } = useAmbientScreenedManuscripts({
    type: "all",
    isActiveTab: true,
  });

  const [modal, setModal] = useState({ open: false, type: null });
  const [confirmRequest, setConfirmRequest] = useState(false);
  const [watchlistRequest, setWatchlistRequest] = useState(false);
  const [comment, setComment] = useState("");
  const [pendingAction, setPendingAction] = useState(null);

  const updateStatus = async (index, status) => {
    const newData = [...data];
    const previousStatus = newData[index].submission_status;
    newData[index].submission_status = status;

    const { match_id, persistent_id } = newData[index];
    try {
      await setTriggerOrMatchStatus(match_id, persistent_id, status);
    } catch {
      showErrorToast();
      newData[index].submission_status = previousStatus;
    } finally {
      setData(newData);
    }
  };

  const handleIconClick = (index, buttonKey) => {
    setSelectedItem({ index, visibleSection: buttonKey });
  };

  const handleRowClick = (index) => {
    if (index === null) {
      return;
    }
    setSelectedItem({ index, visibleSection: SECTION_KEYS.DUPSUB });
  };

  const handleInterceptedAction = (index, value) => {
    if (value === "duplicate confirmed") {
      setPendingAction({ index, value });
      setModal({ open: true, type: "confirm" });
      setConfirmRequest(false);
      return;
    }
    if (value === "suspect paper mill") {
      setPendingAction({ index, value });
      setModal({ open: true, type: "pm" });
      setWatchlistRequest(false);
      setComment("");
      return;
    }
    onActionSelect(index, value);
  };

  const handleModalApply = () => {
    if (modal.type === "confirm") {
      alert(`Confirm action: Request confirmation from other publisher? ${confirmRequest ? "Yes" : "No"}`);
      setModal({ open: false, type: null });
      setPendingAction(null);
      return;
    }
    if (modal.type === "pm") {
      alert(`Paper Mill Suspect: Add to SignalCheck? ${watchlistRequest ? "Yes" : "No"}\nComment: ${comment}`);
      setModal({ open: false, type: null });
      setPendingAction(null);
      return;
    }
  };

  const handleModalClose = () => {
    setModal({ open: false, type: null });
    setPendingAction(null);
  };

  const onActionSelect = (index, status) => {
    updateStatus(index, status);
  };

  const onActionUndo = (index) => {
    updateStatus(index, null);
  };

  const handlePageClick = (event) => {
    setCurrentPage(event.selected);
  };

  const dataWithActions = data?.map((element, index) => ({
    ...element,
    raw_match_status: element.match_status,
    timestamp: convertISOToReadableDate(element.timestamp, "DD MMM YYYY"),
    match_status: <ColorizeStatusText statusText={element.match_status} />,
    action: (
      <div className="d-flex">
        <ApplyAction
          index={index}
          status={data[index].submission_status}
          selectAction={handleInterceptedAction}
          undoAction={onActionUndo}
          options={dropdownOptions}
        />
      </div>
    ),
    signals: (
      <ReportActionButtons
        signals={element.signals}
        onIconClick={(buttonKey) => handleIconClick(index, buttonKey)}
      />
    ),
    clickEvent: () => handleRowClick(index),
  }));

  const fetchAmbientScreeningDataIfNeeded = async (index) => {
    const { persistent_id, signals } = data[index];
    if (persistent_id && signals?.some((s) => s !== "duplicateSubmission")) {
      try {
        showLoader();
        const { data: fetchedData } = await getAmbientScreeningSubmissionById(
          persistent_id
        );
        setFetchedData(fetchedData);
      } finally {
        hideLoader();
      }
    }
  };

  useEffect(() => {
    if (selectedItem.index !== null) {
      fetchAmbientScreeningDataIfNeeded(selectedItem.index);
    } else {
      setFetchedData(null);
    }
  }, [selectedItem, data]);

  useEffect(() => {
    const trimmedValue = searchInput.trim();
    if ((!searchTerm && !trimmedValue) || (trimmedValue === searchTerm)) return;
    setSearchTerm(trimmedValue);
  }, [searchInput, searchTerm, setSearchTerm]);

  const resetModalState = () => {
    setFetchedData(null);
    setSelectedItem({
      index: null,
      visibleSection: SECTION_KEYS.ALL,
    });
  };

  const hasNonDupSubSignals =
    selectedItem.index !== null &&
    !isLoading &&
    data[selectedItem.index]?.signals?.some(
      (x) => x !== "duplicateSubmissions"
    );

  const shouldRenderModal =
    selectedItem.index !== null &&
    (fetchedData || data[selectedItem.index]) &&
    (hasNonDupSubSignals ? fetchedData : true);

  return (
    <>
      {modal.open && modal.type === "confirm" && (
        <ModalWrapper isOpen={true} onClose={handleModalClose} title="Confirm Manuscript" modalClassSize="modal-md">
          <div className="mb-3">Do you want to request confirmation from the other publisher?</div>
          <div className="mb-3">
            <label>
              <input
                type="checkbox"
                checked={confirmRequest}
                onChange={e => setConfirmRequest(e.target.checked)}
              />
              <span className="ms-2">Request confirmation</span>
            </label>
          </div>
          <button className="btn btn-primary" onClick={handleModalApply}>Apply</button>
        </ModalWrapper>
      )}
      {modal.open && modal.type === "pm" && (
        <ModalWrapper isOpen={true} onClose={handleModalClose} title="Paper Mill Suspect" modalClassSize="modal-md">
          <div className="mb-3">Do you want to also add this to the SignalCheck?</div>
          <div className="mb-3">
            <label>
              <input
                type="checkbox"
                checked={watchlistRequest}
                onChange={e => setWatchlistRequest(e.target.checked)}
              />
              <span className="ms-2">Add to SignalCheck</span>
            </label>
          </div>
          <div className="mb-3">
            <label>Comment:</label>
            <textarea
              className="form-control"
              value={comment}
              onChange={e => setComment(e.target.value)}
              rows={3}
            />
          </div>
          <button className="btn btn-primary" onClick={handleModalApply}>Apply</button>
        </ModalWrapper>
      )}
      {shouldRenderModal && (
        <SignalReportModal
          dupSubData={data[selectedItem.index]}
          contentData={fetchedData}
          visibleSection={selectedItem.visibleSection}
          onClose={resetModalState}
          setTriggerOrMatchStatus={setTriggerOrMatchStatus}
          setSubmissionStatus={setSubmissionStatus}
          fetchData={fetchData}
        />
      )}
      {error && <ErrorMessage />}
      {!error && (
        <>
          <DataTable
            columns={columns}
            rows={dataWithActions}
            customNoDataComponent={isInboxTabSelected ? EmptyInbox : null}
            isLoading={isLoading}
          />
          <Pagination
            handlePageClick={handlePageClick}
            pageCount={pageCount}
            currentPage={currentPage}
          />
        </>
      )}
    </>
  );
};

export default ArchivedManuscriptsTab;